import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';

import Tooltip from '~components/common/Tooltip';
import Info from '~components/Icon/Info';
import { LNB_LIST } from '~features/home/LNB/constants';
import useGetRanking from '~hooks/query/home/useRanking';
import useMedia from '~hooks/useMedia';
import { formattedMDTimeDate } from '~utils/index';

import { MENU_TYPE } from '../constants';
import SubCategoryTabs from '../LNB/SubCategoryTabs';
import { useGetCatetoryIndustriesMenu } from './hooks';
import RankingCard from './RankingCard';
import SkeletonRankingCard from './SkeletonRankingCard';
import * as S from './style';

interface Props {
  selectedMenu: MENU_TYPE;
}

function Ranking({ selectedMenu }: Props) {
  const router = useRouter();
  const { isMobile, isDesktop } = useMedia();
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<string>('TOTAL');
  const industriesMenu = useGetCatetoryIndustriesMenu(selectedMenu);

  const handleClickSubCategory = (subCategoryId: string) => {
    const params = new URLSearchParams({ selectedMenu, subCategory: subCategoryId });
    setSelectedSubCategoryId(subCategoryId);

    router.replace(
      {
        pathname: '/',
        search: params.toString(),
      },
      undefined,
      { scroll: false },
    );
  };

  const {
    mappedData: mappedRankingData = [],
    status,
    data: selectedRanking,
  } = useGetRanking(selectedMenu, selectedSubCategoryId);

  const { text } = useMemo(
    () => LNB_LIST.find((lnb) => lnb.id === selectedMenu) || { text: '' },
    [selectedMenu],
  );

  useEffect(() => {
    setSelectedSubCategoryId((router.query.subCategory as string) ?? 'TOTAL');
  }, [router.asPath]);

  return (
    <S.RankingWrapper>
      {isDesktop && <S.Title>{text}</S.Title>}
      {isDesktop && industriesMenu && industriesMenu.length > 0 && (
        <SubCategoryTabs
          selectedMenuId={selectedSubCategoryId}
          menuList={industriesMenu}
          onClick={handleClickSubCategory}
        />
      )}
      <S.UpdateDateWrapper>
        <S.UpdateDate>
          {selectedRanking &&
            status === 'success' &&
            `${formattedMDTimeDate(
              selectedRanking.version && new Date(selectedRanking.version),
            )} 기준`}
          <Tooltip
            position={isMobile ? 'center' : 'right'}
            tooltipText={
              '국민연금, 금감원, 알리오, 클린아이 등의 데이터를 기반으로 추정된 값입니다. 참고 자료로만 활용하시길 바랍니다.'
            }
            width={isMobile ? 262 : 282}>
            <Info />
          </Tooltip>
        </S.UpdateDate>
      </S.UpdateDateWrapper>
      {selectedRanking && selectedRanking.data && selectedRanking.data.length > 0 ? (
        <S.RankingBody>
          {mappedRankingData.map((group, groupIndex) => {
            if (!group) return null;

            return (
              <div key={`ranking_group_${groupIndex}`}>
                <S.RankingCardGroup>
                  {group.map((company, rankIndex) => {
                    return (
                      <RankingCard
                        {...company}
                        key={`salary_agv_ranking_${company.PK_NM_HASH}`}
                        eventTitle={text}
                      />
                    );
                  })}
                </S.RankingCardGroup>
                {groupIndex < 2 && (
                  <a
                    data-attribute-id='home__banner__click'
                    data-banner-type='ads'
                    data-banner-location={`inner-${groupIndex + 1}`}>
                    {isMobile ? (
                      <S.GoogleAdsBanner bannerType='mobileBanner' slotId='8076432031' />
                    ) : (
                      <S.GoogleAdsBanner bannerType='leaderBoard' slotId='8397610632' />
                    )}
                  </a>
                )}
              </div>
            );
          })}
        </S.RankingBody>
      ) : (
        <S.RankingBody>
          <S.RankingCardGroup>
            {[...Array(10)].map((e, index) => (
              <SkeletonRankingCard key={`skel_group1_${index}`} />
            ))}
          </S.RankingCardGroup>
          <S.RankingCardGroup>
            {[...Array(10)].map((e, index) => (
              <SkeletonRankingCard key={`skel_group2_${index}`} />
            ))}
          </S.RankingCardGroup>
        </S.RankingBody>
      )}
      <Link href='/dashboard'>
        <S.ShowMore data-attribute-id='cta__click' data-cta-type='industryMore'>
          산업군별 더보기 +
        </S.ShowMore>
      </Link>
    </S.RankingWrapper>
  );
}

export default React.memo(Ranking);
